import React, { useEffect } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Squares2X2Icon, CreditCardIcon, UserCircleIcon, ArrowRightOnRectangleIcon, KeyIcon, ShieldCheckIcon, InformationCircleIcon, AdjustmentsHorizontalIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { MoonIcon, SunIcon } from "@heroicons/react/20/solid";
import { useTheme } from "../hooks/useTheme";
import { useAuth } from "../contexts/AuthContext";
import { CreditProvider } from "../contexts/CreditContext";
import Tooltip from "../components/Tooltip";

const Func = ({ children }) => {
  const routerState = useRouterState();
  const { user, signOut, stopImpersonation, isImpersonating, getImpersonationTargetId } = useAuth();
  const { isDark, toggle } = useTheme();

  useEffect(() => {
    if (window.sa_pageview) {
      window.sa_pageview(routerState.location.pathname);
    }
  }, [routerState]);

  const isAiStudio = routerState.location.pathname.startsWith('/app/ai-studio');

  // heroicons
  const menuItems = [
    { name: "Dashboard", href: "/app/dashboard", icon: <Squares2X2Icon className="w-6 h-6" /> },
    { name: "AI Studio", href: "/app/ai-studio", icon: <SparklesIcon className="w-6 h-6" />, special: true },
    { name: "Presets", href: "/app/presets", icon: <AdjustmentsHorizontalIcon className="w-6 h-6" /> },
    { name: "Billing", href: "/app/billing", icon: <CreditCardIcon className="w-6 h-6" /> },
    { name: "API Keys", href: "/app/api-keys", icon: <KeyIcon className="w-6 h-6" /> },
    { name: "Profile", href: "/app/profile", icon: <UserCircleIcon className="w-6 h-6" /> },
  ];

  if (user && user.is_admin) {
    menuItems.push({ name: "Admin", href: "/app/admin", icon: <ShieldCheckIcon className="w-6 h-6" /> });
  }

  const impersonationTargetId = isImpersonating ? getImpersonationTargetId() : null;

  return (
    <CreditProvider>
      <div className="flex flex-col h-screen bg-background text-foreground">
        {isImpersonating && (
          <div className="bg-warning-soft border border-warning-border text-warning-soft-foreground font-semibold p-2 text-center flex justify-center items-center">
            <InformationCircleIcon className="w-5 h-5 mr-2" />
            <span>Impersonating User ID: {impersonationTargetId}</span>
            <button
              onClick={stopImpersonation}
              className="ml-4 bg-danger hover:bg-danger-hover text-danger-foreground font-bold py-1 px-2 rounded text-sm"
            >
              Stop impersonating
            </button>
          </div>
        )}
        <div className="flex flex-grow overflow-hidden">
          <aside className={`bg-background-elevated flex flex-col shrink-0 border-r border-border transition-all duration-300 ${isAiStudio ? 'w-20' : 'w-64'}`}>
            <Link to="/" className={`flex items-center gap-3 text-foreground hover:text-accent transition-all duration-300 group py-6 pl-7`}>
              <svg className="w-6 h-6 flex-shrink-0 text-primary group-hover:text-primary-hover transition-colors duration-200" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h2
                className={`text-xl font-semibold whitespace-nowrap transition-all duration-300 overflow-hidden ${isAiStudio ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100'}`}
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                compress-glb.com
              </h2>
            </Link>
            <nav className="flex-grow flex flex-col gap-1 mt-4">
              {menuItems.map((item) => {
                const isActive = routerState.location.pathname.startsWith(item.href);

                return (
                  <Tooltip key={item.name} text={item.name} show={isAiStudio} className="w-full">
                    <Link
                      to={item.href}
                      className={`w-full flex items-center py-3 transition-all duration-300 border-l-2 pl-7 ${item.special
                        ? isActive
                          ? "text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 border-purple-600 shadow-lg shadow-purple-500/50"
                          : "text-white bg-gradient-to-r from-indigo-500/90 via-purple-500/90 to-pink-500/90 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 border-transparent shadow-md shadow-purple-500/30 hover:shadow-lg hover:shadow-purple-500/40"
                        : isActive
                          ? "text-accent bg-accent/10 border-accent"
                          : "text-muted-foreground hover:text-accent hover:bg-accent/5 border-transparent"
                        }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className={item.special ? "drop-shadow-sm" : ""}>
                        {item.icon}
                      </span>
                      <span className={`transition-all duration-300 whitespace-nowrap overflow-hidden ${isAiStudio ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100 ml-3'} ${item.special ? "font-black uppercase tracking-wider drop-shadow-sm text-sm" : ""}`}>
                        {item.name}
                      </span>
                    </Link>
                  </Tooltip>
                );
              })}
            </nav>
            <div className={`py-3 flex items-center transition-all duration-300 ${isAiStudio ? 'flex-col gap-4 justify-center pb-4' : 'flex-row justify-between px-6'} border-t border-border`}>
              <Tooltip text={isDark ? 'Switch to light mode' : 'Switch to dark mode'} show={isAiStudio} className={isAiStudio ? "justify-center w-full" : ""}>
                <button
                  type="button"
                  onClick={toggle}
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-background-soft transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {isDark ? (
                    <SunIcon className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <MoonIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </Tooltip>

              <Tooltip text="Sign out" show={isAiStudio} className={isAiStudio ? "justify-center w-full" : ""}>
                <button
                  className={`flex items-center transition-all duration-300 rounded-md ${isAiStudio ? 'justify-center h-9 w-9 p-0 hover:bg-background-soft' : 'px-3 py-2 hover:bg-background-soft'} text-muted-foreground hover:text-accent`}
                  onClick={signOut}
                >
                  <ArrowRightOnRectangleIcon className="w-6 h-6" />
                  <span className={`transition-all duration-300 whitespace-nowrap overflow-hidden ${isAiStudio ? 'max-w-0 opacity-0' : 'max-w-[100px] opacity-100 ml-2'}`}>Sign out</span>
                </button>
              </Tooltip>
            </div>
          </aside>
          <main className={`flex-1 overflow-x-hidden overflow-y-auto bg-background ${isAiStudio ? '' : ''}`}>
            {isAiStudio ? (
              children
            ) : (
              <div className="container mx-auto px-6 py-8">{children}</div>
            )}
          </main>
        </div>
      </div>
    </CreditProvider>
  );
};

export default Func;
